import axios from "axios";
import { getAccessToken } from "../Login/Service/LoginService";
import { SERVER_URL } from "./app.config";


const FallbackErrorMessage = "Something went wrong! Please try again after some time";
const EmptyResponseMessage = "Empty Response Received from the Server";

const defaultHeaders = {
	Accept: "application/json",
	"Content-Type": "application/json;charset=UTF-8",
};

function resolveRequestUrl(url, customBaseUrl) {
	if (customBaseUrl) return customBaseUrl + url;
	return SERVER_URL + url;
}

/**
 * Best-effort message from failed axios responses (plain text body, JSON body, or fallback).
 */
function messageFromAxiosError(error) {
	const errData = error?.response?.data;
	if (typeof errData === "string" && errData.trim()) {
		return errData;
	}
	if (errData && typeof errData === "object") {
		return errData.message || errData.error || FallbackErrorMessage;
	}
	return FallbackErrorMessage;
}

/**
 * Authenticated GET returning the response body as axios provides it (no `response.data.data` unwrap).
 * Use for CSV, blobs, or any response that is not the standard JSON envelope.
 *
 * @param {Object} config
 * @param {string} config.url
 * @param {object} [config.params]
 * @param {object} [config.headers] merged with auth headers (same semantics as `get`)
 * @param {string} [config.customBaseUrl]
 * @param {string} [config.responseType] axios responseType (default 'json')
 * @param {string} [config.accept] Accept header (default any)
 */
export const getRaw = async (config) => {
	let {
		url,
		headers,
		params,
		data,
		customBaseUrl,
		responseType = "json",
		accept = "*/*",
	} = config;

	if (data) {
		console.warn("Sending Data through GET Request is prohibited");
	}

	const fullUrl = resolveRequestUrl(url, customBaseUrl);

	try {
		const accessToken = await getAccessToken();

		if (!accessToken) {
			return { error: "Failed to Fetch Access Token" };
		}

		const baseHeaders = {
			Accept: accept,
			"Content-Type": "application/json;charset=UTF-8",
			"Xook-Access-Token": accessToken,
		};

		const options = {
			url: fullUrl,
			method: "get",
			params: params,
			responseType: responseType,
			headers: headers ? Object.assign(headers, baseHeaders) : baseHeaders,
		};

		const response = await axios(options);
		return {
			data: response.data,
			status: response.status,
		};
	} catch (error) {
		console.log(error);
		return {
			error: messageFromAxiosError(error),
		};
	}
};

//Axios does not support body for GET requests
export const get = async (config) => {
	let { url, headers, params, data, customBaseUrl } = config;

	url = resolveRequestUrl(url, customBaseUrl);

	if (data) {
		console.warn("Sending Data through GET Request is prohibited");
	}

	try {
		//Returns the current access token if it has not expired or it will not expire for the next 5 mins
		//If the access token is expired or is expiring it will refresh and send a new token
		//Can be given a param forceRefresh to forcefully refresh the token everytime
		const accessToken = await getAccessToken();

		if (!accessToken) {
			console.log("FAiled to Fetch Access Token");
			return { error: "Failed to Fetch Access Token" };
		}

		defaultHeaders["Xook-Access-Token"] = accessToken;

		const options = {
			url: url,
			method: "get",
			params: params,
			headers: headers ? Object.assign(headers, defaultHeaders) : defaultHeaders,
		};

		const response = await axios(options);
		// const { data: { data: responseData }, status } = response;
		// if (!responseData) {
		// 	console.error(EmptyResponseMessage, url);
		// 	return { error: EmptyResponseMessage };
		// }
		return {
			data: response.data.data,
		};

	} catch (error) {
		console.log(error);
		return {
			error: error?.response?.data?.message || FallbackErrorMessage,
		};
	}
};

/**
 * GET that returns plain text (e.g. CSV) — preset over {@link getRaw} with `responseType: 'text'`.
 */
export const getText = (config) => {
	const { accept, ...rest } = config;
	return getRaw({
		...rest,
		responseType: "text",
		accept: accept ?? "text/csv, text/plain, */*",
	});
};

export const post = async (config) => {
	let { url, headers, data } = config;

	url = SERVER_URL + url

	try {
		//Returns the current access token if it has not expired or it will not expire for the next 5 mins
		//If the access token is expired or is expiring it will refresh and send a new token
		//Can be given a param forceRefresh to forcefully refresh the token everytime
		const accessToken = await getAccessToken();

		if (!accessToken) {
			return { error: "Failed to Fetch Access Token" };
		}

		defaultHeaders["Xook-Access-Token"] = accessToken;

		const options = {
			url: url,
			method: "post",
			data: data,
			headers: headers ? Object.assign(headers, defaultHeaders) : defaultHeaders,
		};

		const response = await axios(options);
		const { data: { data: responseData }, status } = response;
		if (!responseData) {
			console.error(EmptyResponseMessage, url);
			return { error: EmptyResponseMessage };
		}
		return {
			data: responseData,
			status,
		};
		
	} catch (error) {
		console.log(error);
		return {
			error: error?.response?.data?.message || FallbackErrorMessage,
		};
	}
};

export const patch = async (config) => {
	var { url, headers, data } = config;

	url = SERVER_URL + url

	try {
		//Returns the current access token if it has not expired or it will not expire for the next 5 mins
		//If the access token is expired or is expiring it will refresh and send a new token
		//Can be given a param forceRefresh to forcefully refresh the token everytime
		const accessToken = await getAccessToken();

		if (!accessToken) {
			return { error: "Failed to Fetch Access Token" };
		}

		defaultHeaders["Xook-Access-Token"] = accessToken;

		const options = {
			url: url,
			method: "patch",
			data: data,
			headers: headers ? Object.assign(headers, defaultHeaders) : defaultHeaders,
		};

		const response = await axios(options);
		const { data: { data: responseData }, status } = response;
		if (!responseData) {
			console.error(EmptyResponseMessage, url);
			return { error: EmptyResponseMessage };
		}
		return {
			data: responseData,
			status,
		};
		
	} catch (error) {
		console.log(error);
		return {
			error: error?.response?.data?.message || FallbackErrorMessage,
		};
	}
};

export const put = async (config) => {
	var { url, headers, data } = config;

	url = SERVER_URL + url

	try {
		//Returns the current access token if it has not expired or it will not expire for the next 5 mins
		//If the access token is expired or is expiring it will refresh and send a new token
		//Can be given a param forceRefresh to forcefully refresh the token everytime
		const accessToken = await getAccessToken();

		if (!accessToken) {
			return { error: "Failed to Fetch Access Token" };
		}

		defaultHeaders["Xook-Access-Token"] = accessToken;

		const options = {
			url: url,
			method: "put",
			data: data,
			headers: headers ? Object.assign(headers, defaultHeaders) : defaultHeaders,
		};

		const response = await axios(options);
		const { data: { data: responseData }, status } = response;
		if (!responseData) {
			return { error: EmptyResponseMessage };
		}
		return {
			data: responseData,
			status,
		};
		
	} catch (error) {
		console.log(error);
		return {
			error: error?.response?.data?.message || FallbackErrorMessage,
		};
	}
};

export const deleteApi = async (config) => {
	var { url, headers, data } = config;

	url = SERVER_URL + url

	try {
		//Returns the current access token if it has not expired or it will not expire for the next 5 mins
		//If the access token is expired or is expiring it will refresh and send a new token
		//Can be given a param forceRefresh to forcefully refresh the token everytime
		const accessToken = await getAccessToken();

		if (!accessToken) {
			return { error: "Failed to Fetch Access Token" };
		}

		defaultHeaders["Xook-Access-Token"] = accessToken;

		const options = {
			url: url,
			method: "delete",
			data: data,
			headers: headers ? Object.assign(headers, defaultHeaders) : defaultHeaders,
		};

		const response = await axios(options);
		const { data: { data: responseData }, status } = response;
		if (!responseData) {
			console.error(EmptyResponseMessage, url);
			return { error: EmptyResponseMessage };
		}
		return {
			data: responseData,
			status,
		};
		
	} catch (error) {
		console.log(error);
		return {
			error: error?.response?.data?.message || FallbackErrorMessage,
		};
	}
};

export const getApi = async function(a = "",b = "", c = "", d = "", e = "", f = "", g = "") {
	return new Promise((resolve,reject) => {
		setTimeout(() => resolve(true), 1000);
	})
}

export const postApi = async function(a = "",b = "", c = "", d = "", e = "", f = "", g = "") {
	return new Promise((resolve,reject) => {
		setTimeout(() => resolve(true), 1000);
	})
}

export const patchApi = async function(a = "",b = "", c = "", d = "", e = "", f = "", g = "") {
	return new Promise((resolve,reject) => {
		setTimeout(() => resolve(true), 1000);
	})
}
